/**
 * The lookup's matching rules, kept apart from the component so they can be
 * unit-tested (and so Fast Refresh keeps working on LookupField.tsx).
 *
 *  - case and accents never matter ("DÉMO" finds "Demolition");
 *  - every typed word must be found, in any order ("150 labor");
 *  - the best matches lead: the exact label (or keyword), then labels that
 *    START with what was typed, then options where every typed word starts a
 *    word, then the rest that merely contain the words — each tier in the
 *    options' own order, so a list sorted on purpose (pinned, recent, the
 *    role catalogue) keeps that order inside each tier.
 *
 * A "word" starts at the beginning, after a space or after an opening mark
 * such as "(" — not after "-" or ".", which join the parts of a code: typing
 * "150" puts "Demolition (150-000)" ahead of "General labor (100-150)".
 */

export interface Matchable {
  label: string;
  /** Extra words that find the option without being shown (a role's short names). */
  keywords?: readonly string[];
}

/** Lower-case, accents off, one space between words, trimmed. */
export function foldText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

interface Folded { label: string; keywords: string[]; hay: string }
const cache = new WeakMap<object, Folded>();

function folded(o: Matchable): Folded {
  let f = cache.get(o);
  if (!f) {
    const label = foldText(o.label);
    const keywords = (o.keywords ?? []).map(foldText).filter(Boolean);
    f = { label, keywords, hay: [label, ...keywords].join(' | ') };
    cache.set(o, f);
  }
  return f;
}

const isWordChar = (c: string) => /[\p{L}\p{N}]/u.test(c);
/** Characters that join the parts of one word or code ("150-000", "02.200", "s/n"). */
const JOINERS = '-._';

function startsAWord(hay: string, word: string): boolean {
  for (let i = hay.indexOf(word); i !== -1; i = hay.indexOf(word, i + 1)) {
    const before = i === 0 ? '' : hay[i - 1];
    if (before === '' || (!isWordChar(before) && !JOINERS.includes(before))) return true;
  }
  return false;
}

/** 0 exact · 1 starts with the query · 2 every word starts a word · 3 every word is in it · null no match. */
function rankOf(o: Matchable, query: string, words: string[]): number | null {
  const f = folded(o);
  if (!words.every((w) => f.hay.includes(w))) return null;
  if (f.label === query || f.keywords.includes(query)) return 0;
  if (f.label.startsWith(query) || f.keywords.some((k) => k.startsWith(query))) return 1;
  if (words.every((w) => startsAWord(f.hay, w))) return 2;
  return 3;
}

/** The options that match `query`, best first; a blank query keeps them all, in order. */
export function matchOptions<T extends Matchable>(options: readonly T[], query: string): T[] {
  const q = foldText(query);
  if (q === '') return [...options];
  const words = q.split(' ');
  const ranked: { o: T; rank: number; i: number }[] = [];
  options.forEach((o, i) => {
    const rank = rankOf(o, q, words);
    if (rank !== null) ranked.push({ o, rank, i });
  });
  return ranked.sort((a, b) => a.rank - b.rank || a.i - b.i).map((r) => r.o);
}
